/* 黃蓋（字公覆）
 * v0.5.7 重製：耗血放招（res: 'none'，技能資料 hpCost）、照泰達米爾「壓血求強度」，設計見 docs/HUANGGAI-REWORK.md
 * 受傷或自己耗血都累積「傷痕」（u.scarN，最多 10），按 E 以自己為中心地裂一次打出；滿血反而不強 */
import { SG, L } from '../../core/kit.ts';
import { defineHero, hook, S } from '../define.ts';

const ID = 'huanggai', SCAR_MAX = 10, E = 2;

export default defineHero({
  id: ID, name: '黃蓋', sub: '字公覆', faction: 'wu', role: '坦克', weapon: 'axe', res: 'none',
  look: { wart: 'whip', hair: '#e9e5dc', beard: 'long', bulk: 1.12, weaponName: '鐵鞭', fx: '#ff8a3a' },
  skills: [
    ['苦肉計', 'buff', '耗目前生命 20%，鐵鞭自笞：5 秒攻速 +{20/30/40/50}%。扣掉的血也算進「傷痕」。', { x: { k: 'selfharm', as: [20, 30, 40, 50], dur: 5 }, aim: S(0), cd: 12, hpCost: 20, el: 'fire' }],
    ['詐降', 'roar', '大喝一聲，嘲諷周圍 300 內的敵人 {1/1.2/1.4/1.6} 秒，讓他們只能朝黃蓋走來。', { x: { k: 'feign', taunt: [1, 1.2, 1.4, 1.6], r: 300, at: 0.15 }, aim: S(300), cd: [16, 15, 14, 13], el: null }],
    ['傷痕', 'roar', '被敵人打傷或自己耗血時，每累計扣掉 {11/9/7/5}% 最大生命得 1 道傷痕（最多 10 道，陣亡清空）。施放：以自己為中心地裂，對 300 內的敵人造成「傷痕數 ×（{15/18/21/24} ＋ 最大生命 2%）」傷害，傷痕清空。',
      { x: { k: 'scarquake', per: [11, 9, 7, 5], dmg: [15, 18, 21, 24], hpP: 2, r: 300 }, aim: S(300), cd: 0.5, free: true, el: null, stack: 'scarN', max: SCAR_MAX }],
    ['浴火', 'buff', '5 秒內生命不會低於 1，施放時立刻得到 {3/4/5} 道傷痕。', { x: { k: 'bathfire', dur: 5, scars: [3, 4, 5] }, aim: S(0), cd: [110, 100, 90], el: 'fire', shout: '老夫這身傷，就是破曹的火！' }],
  ],
  skillOrder: [2, 0, 1], // 電腦自動配點：傷痕優先（核心招；預設 Q→W→E 要到 10 級後才點得到 E）
  passive: ['苦肉', '普攻額外造成真實傷害，等於已損失生命的 {2/3/4}%。', { lv: [1, 7, 17] }],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    // 苦肉計：耗血（tryCast 已扣）換攻速
    selfharm(u, a, X) {
      this.addBuff(u, { id: a.sk.name, t: X.dur, as: X.as });
      this.fx({ k: 'fire', x: u.x, y: u.y, pal: 'fire', small: 1 });
    },
    // 詐降：範圍嘲諷（武將、小兵、野怪都會被拉過來）
    feign(u, a, X) {
      this.enemiesNear(u, u.x, u.y, X.r).forEach((t) => { if (!t.structure) this.applyCC(u, t, { taunt: X.taunt }); });
      this.fx({ k: 'slam', x: u.x, y: u.y, r: X.r, col: u.spec.fx });
    },
    // 傷痕：地裂，把累積的傷痕一次打出去；沒有傷痕或附近沒有敵人就不放（傷痕留著）
    scarquake(u, a, X) {
      const n = u.scarN || 0;
      const foes = this.enemiesNear(u, u.x, u.y, X.r).filter((t) => !t.structure);
      if (!n || !foes.length) { u.cd[a.i] = 0; if (u.human) this.tell(u, n ? '附近沒有敵人' : '還沒有傷痕', 'warn'); return; }
      const dmg = n * (X.dmg + u.maxHp * X.hpP / 100);
      foes.forEach((t) => this.damage(u, t, dmg, { spell: true, stagger: 0.35 }));
      u.scarN = 0; u.scarHp = 0;
      this.fx({ k: 'slam', x: u.x, y: u.y, r: X.r, col: '#ff6a3a' });
      if (u.human || foes.some((t) => t.human)) this.dmgEvents.push({ x: u.x, y: u.y - 80, text: `傷痕 ×${n}`, team: 1 - u.team, src: u.id, tgt: u.id });
    },
    // 浴火：泰達米爾的不死怒火（增益以招式名稱當 id，畫面上是大招光環，連線也會帶）
    bathfire(u, a, X) {
      u.bathT = this.t + X.dur;
      this.addBuff(u, { id: a.sk.name, t: X.dur, src: u, col: '#ff6a3a' });
      if (u.skillLv[E]) u.scarN = Math.min(SCAR_MAX, (u.scarN || 0) + X.scars); // 還沒學傷痕就不給（存了也用不掉）
      this.say(`${u.name}：「${a.sk.shout}」`, 'big');
      this.fx({ k: 'fire', x: u.x, y: u.y, pal: 'fire' });
    },
  },
  methods: {
    // 累積傷痕：扣掉的生命累加，每到門檻得 1 道；滿 10 道就不再累加
    addScar(u, lost) {
      const lv = u.skillLv[E];
      if (!lv || lost <= 0 || (u.scarN || 0) >= SCAR_MAX) return;
      const th = u.maxHp * L(u.spec.skills[E].x.per, lv) / 100;
      u.scarHp = (u.scarHp || 0) + lost;
      while (u.scarHp >= th && (u.scarN || 0) < SCAR_MAX) { u.scarHp -= th; u.scarN = (u.scarN || 0) + 1; }
      if (u.scarN >= SCAR_MAX) u.scarHp = 0;
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 傷痕：被敵人打傷
    hook('damage.after', 56, function (c, lost) { const t = c.t; if (t.spec && t.spec.id === ID && t.kind === 'hero' && c.own && c.own.team !== t.team) this.addScar(t, lost); }),
    // 傷痕：自己耗血（tryCast 扣的量在 u.hpPaid）
    hook('cast.after', 52, function (u, i, sk) { if (u.spec.id === ID && sk && sk.hpCost) this.addScar(u, u.hpPaid || 0); }),
    // 浴火：期間生命不會低於 1
    hook('preKill', 13, function (src, t) { if (t.bathT > this.t) { t.hp = 1; return true; } }),
    // 天賦「苦肉」：普攻追加真實傷害＝已損失生命 × 2/3/4%（不打建築）
    hook('onHit', 23, function (u, t) {
      if (u.spec.id !== ID || u.kind !== 'hero' || t.structure || !t.alive) return;
      const p = [0.02, 0.03, 0.04][SG.talTier(u.spec, u.lv)], v = (u.maxHp - u.hp) * p;
      if (v >= 1) this.damage(u, t, v, { pure: true, quiet: true });
    }),
    // 陣亡清空傷痕
    hook('kill.after', 15, function (src, t) { if (t.spec && t.spec.id === ID && !t.alive) { t.scarN = 0; t.scarHp = 0; t.bathT = 0; } }),
    hook('revive', 45, function (u) { u.scarN = 0; u.scarHp = 0; u.bathT = 0; }),
  ],
  // 電腦什麼時候放這招（耗血招在生命 25% 以下不放：core/ai.ts）
  ai: {
    selfharm: ({ d, hero, hpR }) => hero && d < 350 && hpR > 0.35,
    feign: ({ u, foeHeroes }) => foeHeroes(280) > 0 && (u.scarN || 0) < 6, // 傷痕多了就讓給 E（電腦照 R、Q、W、E 的順序挑）
    scarquake: ({ u, hpR, foeHeroes }) => (u.scarN || 0) >= (hpR < 0.35 ? 2 : 4) && foeHeroes(280) > 0,
    bathfire: ({ hpR, foeHeroes }) => hpR < 0.3 && foeHeroes(600) > 0,
  },
});
